import * as React from 'react';
import { Form, Col, Row, Button } from 'react-bootstrap';
import {
    Bloodwork,
    BloodworkNote,
    Section,
    BloodworkAnalysis,
    FurtherConsiderations,
    PanelMarker,
} from '../../models';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import {
    SectionNotesDigest,
    buildSectionDigest,
    digestTabToMarkdown,
    DigestTab,
} from './section-notes-digest';
import {
    MarkdownToolbar,
    wrapSelection,
    formatMarkerForKeydown,
    FormatMarker,
} from './note-markdown';

type Props = {
    section: Section;
    title: string;
    bloodwork: Bloodwork;
    bloodworkEntryStore: BloodworkEntryStore;
    // When these are provided (the per-section Notes tab), a "gather from tabs"
    // digest renders below Save. Omitted for the global Insights note, which has
    // no single section to summarise, so the digest simply doesn't appear there.
    bloodworkAnalysis?: BloodworkAnalysis;
    furtherConsiderations?: FurtherConsiderations;
    panelMarkers?: PanelMarker[];
};

type State = {
    bloodworkNote: BloodworkNote;
};

export class BloodworkNotes extends React.Component<Props, State> {
    state: State = {
        bloodworkNote: this.initBloodworkNote(this.props.section, this.props.bloodwork),
    }

    private wrapperEl: HTMLDivElement | null = null;
    private static MIN_HEIGHT_PX = 156;

    componentDidMount() {
        this.fitToContent();
    }

    componentWillReceiveProps(nextProps) {
        // Only re-load from the saved model when the SECTION changes. Re-loading
        // on every prop change would overwrite the practitioner's unsaved typing
        // (which lives in local state, not the model) on any unrelated ancestor
        // re-render.
        if (nextProps.section === this.props.section) return;
        const { section, bloodwork } = nextProps;
        this.setState({
            bloodworkNote: this.initBloodworkNote(section, bloodwork),
        }, this.fitToContent);
    }

    initBloodworkNote(section: string, bloodwork: Bloodwork): BloodworkNote {
        if (bloodwork && bloodwork.bloodworkNotes) {
            let bloodworkNote = bloodwork.bloodworkNotes.findNote(section);
            if (!bloodworkNote) {
                bloodworkNote = bloodwork.bloodworkNotes.addNote(section, '');
            }
            return new BloodworkNote(bloodworkNote);
        }
        return new BloodworkNote();
    }

    setWrapperRef = (el: HTMLDivElement | null) => {
        this.wrapperEl = el;
    }

    fitToContent = () => {
        const el = this.wrapperEl?.querySelector('textarea') as HTMLTextAreaElement | null;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, BloodworkNotes.MIN_HEIGHT_PX)}px`;
        el.style.overflow = 'hidden';
    }

    handleInputChange = (event: any) => {
        const { section } = this.props;
        const value = event.target.value;
        this.setState({
            bloodworkNote: new BloodworkNote({section, text: value}),
        });
    }

    saveOrUpdate = (event: any) => {
        const { section, bloodwork, bloodworkEntryStore } = this.props;
        event.preventDefault();
        if (!bloodwork || !bloodwork.bloodworkNotes) return;
        // findNote can be undefined if this section's note was never created;
        // create it rather than assigning .text on undefined.
        let bloodworkNote = bloodwork.bloodworkNotes.findNote(section);
        if (!bloodworkNote) {
            bloodworkNote = bloodwork.bloodworkNotes.addNote(section, '');
        }
        bloodworkNote.text = this.state.bloodworkNote.text;
        bloodworkEntryStore.saveBloodwork(bloodwork).catch((err) => {
            console.error('Failed to save bloodwork note', err);
            alert('Failed to save your note. Please try again.');
        });
        this.fitToContent();
    }

    // ---- "gather from tabs" digest wiring -------------------------------

    private digestTabs(): DigestTab[] {
        const { section, bloodworkAnalysis, furtherConsiderations, panelMarkers } = this.props;
        if (!bloodworkAnalysis || !furtherConsiderations) return [];
        return buildSectionDigest(section, bloodworkAnalysis, furtherConsiderations, panelMarkers || []);
    }

    // Append with a blank line before the block when the note isn't already
    // separated, so inserted tabs read as their own paragraphs.
    private static appendBlock(existing: string, block: string): string {
        let v = existing || '';
        if (v && !v.endsWith('\n\n')) v += v.endsWith('\n') ? '\n' : '\n\n';
        return v + block;
    }

    // Remove the LAST occurrence of a block plus the blank-line separator we
    // appended after it, leaving the rest of the note's spacing untouched.
    // (Last, not first, so we never delete an identical block the practitioner
    // pasted earlier; scoped to the seam so we never reflow their own text.)
    private static removeBlock(existing: string, block: string): string {
        const idx = existing.lastIndexOf(block);
        if (idx === -1) return existing;
        const before = existing.slice(0, idx);
        const after = existing.slice(idx + block.length).replace(/^\n\n?/, '');
        return before + after;
    }

    // A tab counts as "inserted" when the note currently contains its exact
    // block. Deriving this from the note text (rather than tracking separate
    // state) keeps the Insert⇄Added toggle correct across Save and page reload,
    // so re-inserting can't silently append a duplicate.
    private isInserted(tab: DigestTab): boolean {
        return (this.state.bloodworkNote.text || '').includes(digestTabToMarkdown(tab));
    }

    private setNoteText = (text: string) => {
        const { section } = this.props;
        this.setState(
            { bloodworkNote: new BloodworkNote({ section, text }) },
            this.fitToContent,
        );
    };

    // ---- rich-text (inline markdown) formatting ------------------------

    private getTextarea = (): HTMLTextAreaElement | null =>
        (this.wrapperEl?.querySelector('textarea') as HTMLTextAreaElement | null) ?? null;

    // Set the note text, then restore the selection the toolbar/shortcut asked
    // for once React has re-rendered the (controlled) textarea.
    private applyFormat = (value: string, selStart: number, selEnd: number) => {
        const { section } = this.props;
        this.setState(
            { bloodworkNote: new BloodworkNote({ section, text: value }) },
            () => {
                this.fitToContent();
                const el = this.getTextarea();
                if (el) {
                    requestAnimationFrame(() => {
                        el.focus();
                        el.setSelectionRange(selStart, selEnd);
                    });
                }
            },
        );
    };

    private wrapWith = (marker: FormatMarker) => {
        const el = this.getTextarea();
        if (!el) return;
        const { value, selStart, selEnd } = wrapSelection(el, marker);
        this.applyFormat(value, selStart, selEnd);
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const marker = formatMarkerForKeydown(e);
        if (marker) {
            e.preventDefault();
            this.wrapWith(marker);
        }
    };

    private handleToggleTab = (tab: DigestTab) => {
        const block = digestTabToMarkdown(tab);
        const current = this.state.bloodworkNote.text || '';
        const nextText = current.includes(block)
            ? BloodworkNotes.removeBlock(current, block)
            : BloodworkNotes.appendBlock(current, `${block}\n\n`);
        this.setNoteText(nextText);
    };

    // Insert a whole section (its bold heading + every bullet).
    private handleInsertGroup = (heading: string, items: string[]) => {
        const current = this.state.bloodworkNote.text || '';
        const block = `**${heading}**\n${items.map(i => `• ${i}`).join('\n')}\n\n`;
        this.setNoteText(BloodworkNotes.appendBlock(current, block));
    };

    // True when the most recent bold heading in the note is exactly `headingLine`
    // — so adding several bullets from one section stacks them under a single
    // heading instead of repeating it.
    private static lastHeadingMatches(text: string, headingLine: string): boolean {
        const lines = text.split('\n');
        for (let i = lines.length - 1; i >= 0; i--) {
            const t = lines[i].trim();
            if (/^\*\*.+\*\*$/.test(t)) return t === headingLine;
        }
        return false;
    }

    // Insert one bullet, carrying its section heading so the note keeps context.
    // The heading is added only when it isn't already the current section.
    private handleInsertLine = (item: string, heading: string) => {
        const current = this.state.bloodworkNote.text || '';
        const headingLine = `**${heading}**`;
        if (BloodworkNotes.lastHeadingMatches(current, headingLine)) {
            let v = current;
            if (v && !v.endsWith('\n')) v += '\n';
            this.setNoteText(`${v}• ${item}\n`);
        } else {
            this.setNoteText(
                BloodworkNotes.appendBlock(current, `${headingLine}\n• ${item}\n`),
            );
        }
    };

    private handleInsertAll = () => {
        let text = this.state.bloodworkNote.text || '';
        this.digestTabs().forEach(tab => {
            const block = digestTabToMarkdown(tab);
            if (text.includes(block)) return;
            text = BloodworkNotes.appendBlock(text, `${block}\n\n`);
        });
        this.setNoteText(text);
    };

    render() {
        const { title, bloodworkAnalysis, furtherConsiderations } = this.props;
        const tabs = this.digestTabs();
        const insertedKeys = tabs.filter(t => this.isInserted(t)).map(t => t.key);
        return (
            <>
                <Form id="notes-form">
                    <Form.Group controlId="exampleForm.ControlTextarea1">
                        <Form.Label>{title}</Form.Label>
                        <MarkdownToolbar
                            getTextarea={this.getTextarea}
                            onApply={this.applyFormat}
                        />
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 6 }}>
                            Formatting:&nbsp;<code>## Section</code>&nbsp;for section headings,&nbsp;
                            <code>**bold**</code>,&nbsp;<code>*italic*</code>,&nbsp;<code>_underline_</code>.
                            &nbsp;These render formatted in the Section Notes summary.
                        </div>
                        <div ref={this.setWrapperRef}>
                            <Form.Control as="textarea" rows="6"
                                name="notes-text"
                                value={this.state.bloodworkNote.text ?? ''}
                                onChange={this.handleInputChange}
                                onKeyDown={this.handleKeyDown}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group as={Row}>
                        <Col>
                            <Button id="submitButton" type="submit" onClick={this.saveOrUpdate}>Save</Button>
                        </Col>
                    </Form.Group>
                </Form>
                {bloodworkAnalysis && furtherConsiderations && tabs.length > 0 && (
                    <SectionNotesDigest
                        tabs={tabs}
                        insertedKeys={insertedKeys}
                        onToggleTab={this.handleToggleTab}
                        onInsertGroup={this.handleInsertGroup}
                        onInsertLine={this.handleInsertLine}
                        onInsertAll={this.handleInsertAll}
                    />
                )}
            </>
        );
    }
}